import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { PROS_STRINGS_ES } from 'src/features/pros/pros.strings.es';
import type { ProsStrings } from 'src/features/pros/pros.strings';
import { PROS_STRINGS } from 'src/features/pros/pros.strings';

/**
 * Which language the Pro network page is being read in.
 *
 * The language is the route: `/pros` is English, `/es/pros` is Spanish. It was
 * a `?lang=es` query param first, which switched instantly but gave Google one
 * URL with two languages on it — so the Spanish copy was never going to be
 * indexed. A path segment is the crawlable form, and each route carries its
 * own canonical and `hreflang` pair.
 *
 * The locale leads the path — `/<lang>/<page>` — so adding the next translated
 * page is one file under `pages/es/`, not a locale route nested inside every
 * English page.
 *
 * Both locales' copy is still bundled together, so switching is a client-side
 * route change between two static pages with nothing to fetch.
 */

type ProsLanguage = 'en' | 'es';

/** Route for each language. Also what the toggle navigates between. */
const PROS_ROUTES: Record<ProsLanguage, string> = {
  en: '/pros',
  es: '/es/pros',
};

const STRINGS: Record<ProsLanguage, ProsStrings> = {
  en: PROS_STRINGS,
  es: PROS_STRINGS_ES,
};

type ProsLanguageValue = {
  language: ProsLanguage;
  /** Copy for the current language. */
  strings: ProsStrings;
  /** Always the English copy. For anything that must not move with the
   *  language: analytics labels, and the structured-data checks that key off
   *  an English literal. */
  englishStrings: ProsStrings;
  /** The other language, and the route it lives at — what the toggle links
   *  to. A real href rather than a click handler, so the Spanish page is
   *  something a crawler can follow and a reader can open in a new tab. */
  alternate: { language: ProsLanguage; href: string };
};

const ProsLanguageContext = createContext<ProsLanguageValue | null>(null);

/**
 * The language a Pro network path is in. Anything that is not the Spanish
 * route is English, including `/pros` itself.
 *
 * Exported for the Apply modal, which the modal manager renders outside this
 * provider and so has to resolve the language from the route itself.
 */
export function prosLanguageFromPath(pathname: string): ProsLanguage {
  return pathname === PROS_ROUTES.es ? 'es' : 'en';
}

export function ProsLanguageProvider({ children }: { children: ReactNode }) {
  const { pathname } = useRouter();
  const language = prosLanguageFromPath(pathname);

  const value = useMemo<ProsLanguageValue>(
    () => ({
      language,
      strings: STRINGS[language],
      englishStrings: PROS_STRINGS,
      alternate: {
        language: language === 'es' ? 'en' : 'es',
        href: language === 'es' ? PROS_ROUTES.en : PROS_ROUTES.es,
      },
    }),
    [language],
  );

  return (
    <ProsLanguageContext.Provider value={value}>
      {children}
    </ProsLanguageContext.Provider>
  );
}

export function useProsLanguage(): ProsLanguageValue {
  const value = useContext(ProsLanguageContext);

  if (!value) {
    throw new Error(
      'useProsLanguage must be used inside a ProsLanguageProvider',
    );
  }

  return value;
}
